import { TestId } from '@trello/test-ids';
import classNames from 'classnames';
import React from 'react';
import styles from './Button.less';
import Spinner from '@atlaskit/spinner';

const toCamelCase = (str: string) =>
  str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

export enum ButtonSizes {
  DEFAULT = 'default',
  WIDE = 'wide',
  FULLWIDTH = 'fullwidth',
}

export type ButtonAppearances =
  | 'default'
  | 'primary'
  | 'danger'
  | 'transparent'
  | 'transparent-dark'
  | 'link'
  | 'subtle'
  | 'subtle-link'
  // !deprecate
  | 'icon'
  | 'disabled';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The visible appearance of a button.
   *
   * @default 'default'
   */
  appearance?: ButtonAppearances;
  /** A string of classnames to be applied
   *
   */
  className?: string;
  /**
   * The content of the button.
   * @default null
   */
  children?: React.ReactNode;
  /** A function to fire when the element is clicked
   *
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** An icon to render after the button content
   *
   */
  iconAfter?: JSX.Element;
  /** An icon to render before the button content.
   * If this prop is provided with no other children, the
   * icon will be styled as if it is a square "Icon Button"
   *
   */
  iconBefore?: JSX.Element;
  /**
   * If `true`, a Spinner will be rendered in the button in place of the content
   * @default false
   */
  isLoading?: boolean;
  /**
   * If `true`, the button will be disabled.
   * @default false
   */
  isDisabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  shouldFitContainer?: boolean;
  /**
   * The size of the button.
   * @default '"default"'
   */
  size?: ButtonSizes;
  /**
   * A string to help identify the component during integration tests.
   */
  testId?: TestId;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      onClick,
      className,
      appearance = 'default',
      size = ButtonSizes.DEFAULT,
      testId,
      iconAfter,
      iconBefore,
      isLoading,
      isDisabled,
      shouldFitContainer,
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button
      // the default behavior is actually "submit", but we are retaining this
      // behavior from the previous component. This is something to revisit if
      // we want this component to be analogous to the native html <button>
      // AtlasKit Button also has similar behavior
      type = 'button',
      ...buttonAttrs
    },
    ref,
  ) => {
    const { disabled } = buttonAttrs;

    if (shouldFitContainer) {
      size = ButtonSizes.FULLWIDTH;
    }

    if (isDisabled) {
      appearance = 'disabled';
    }

    let buttonContent = children;

    if (iconAfter || iconBefore) {
      buttonContent = (
        <>
          {iconBefore &&
            React.cloneElement(iconBefore, {
              color: iconBefore.props.color || 'dark',
              dangerous_className: classNames(
                {
                  [styles.iconOnly]: !children,
                  [styles.iconBefore]: children,
                },
                iconBefore.props.dangerous_className,
              ),
            })}
          {children}
          {iconAfter &&
            React.cloneElement(iconAfter, {
              color: iconAfter.props.color || 'dark',
              dangerous_className: classNames(
                styles.iconAfter,
                iconAfter.props.dangerous_className,
              ),
            })}
        </>
      );
    }

    const handleFocus = (
      e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e && e.preventDefault();
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isDisabled}
        className={classNames(
          styles.button,
          className,
          styles[toCamelCase(appearance)],
          styles[size],
          {
            [styles['disabled']]: disabled || isDisabled,
            [styles.iconButton]: iconBefore && !children,
          },
        )}
        onClick={onClick}
        // a11y behavior to keep focus ring on key events, but remove it after click
        // https://github.com/react-bootstrap/react-bootstrap/issues/1300#issuecomment-386425763
        onMouseDown={handleFocus}
        onKeyUp={(e) => {
          if (e.keyCode === 13 || e.keyCode === 32) {
            handleFocus();
          }
        }}
        data-test-id={testId}
        type={type}
        {...buttonAttrs}
      >
        {isLoading ? <Spinner /> : buttonContent}
      </button>
    );
  },
);
Button.displayName = 'Button';

export interface IconButtonProps extends ButtonProps {
  icon: JSX.Element;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, icon, onClick, size, className, ...buttonAttrs }, ref) => {
    const {
      color,
      size: iconSize,
      dangerous_className: iconClass,
    } = icon.props;
    const iconClassName = classNames(
      {
        [styles.largeIcon]: iconSize === 'large',
        [styles.smallIcon]: iconSize === 'small',
        [styles['button-icon-dark']]: color === 'dark',
      },
      iconClass,
    );

    return (
      <Button
        ref={ref}
        size={size}
        onClick={onClick}
        className={classNames(styles.iconButton, className, {
          [styles.iconButtonSmall]: iconSize === 'small',
          [styles.iconButtonLarge]: iconSize === 'large',
        })}
        {...buttonAttrs}
      >
        {React.cloneElement(icon, {
          dangerous_className: iconClassName,
          size: iconSize,
          color: color,
        })}
        {children && (
          <span className={styles.iconButtonContents}>{children}</span>
        )}
      </Button>
    );
  },
);

interface LinkProps {
  children: React.ReactNode;
  link: string;
  isPrimary?: boolean;
  onClick?: React.EventHandler<React.MouseEvent<HTMLElement>>;
  openInNewTab?: boolean;
  className?: string;
}

export const ButtonLink = ({
  children,
  link,
  isPrimary,
  onClick,
  openInNewTab,
  className,
}: LinkProps) => {
  const buttonStyle: string = isPrimary
    ? styles.primaryButtonLink
    : styles.defaultButtonLink;
  let linkTarget: string | undefined = undefined;
  let rel: string | undefined = undefined;

  if (openInNewTab) {
    linkTarget = '_blank';
    rel = 'noreferrer';
  }

  return (
    <a
      href={link}
      className={classNames(buttonStyle, className)}
      onClick={onClick}
      target={linkTarget}
      rel={rel}
    >
      <span>{children}</span>
    </a>
  );
};
